import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/browser';

import * as recordingsApi from '@hotjar/webapp/domains/recordings/api/recordings';
import type { SiteId } from '@hotjar/site';
import type { RecordingId, WorthWatchingAnswer } from '@hotjar/webapp/domains/recordings/model';

export enum STATUS {
  INITIAL,
  LOADING,
  ERROR,
  SUCCESS,
}

type IsSucess = boolean;

interface UseRecordingsProps {
  siteId: SiteId;
  recordingId: RecordingId;
}

type UseRecordingsHook = [
  { status: STATUS },
  {
    setWorthWatching: (worthWatching: WorthWatchingAnswer) => Promise<IsSucess>;
  },
];

export const useRecordingsApi = ({
  siteId,
  recordingId,
}: UseRecordingsProps): UseRecordingsHook => {
  const [status, setStatus] = useState(STATUS.INITIAL);

  const setWorthWatching = useCallback(
    async (worthWatching: WorthWatchingAnswer) => {
      let isSuccess = true;
      try {
        setStatus(STATUS.LOADING);
        await recordingsApi.setWorthWatching(siteId, recordingId, worthWatching);
      } catch (e) {
        setStatus(STATUS.ERROR);
        isSuccess = false;
        Sentry.captureMessage(`Could not update if the recording was worth watching. ${e}`);
      } finally {
        setStatus(STATUS.SUCCESS);
      }
      return isSuccess;
    },
    [siteId, recordingId],
  );

  return [
    { status },
    {
      setWorthWatching,
    },
  ];
};
